var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import { DataFrame } from '../../../model/DataFrame.js';
import { poisCreateFromDataFrame } from '../../../utils/poi.js';
import { basicPoiFields } from '../../../model/Field.js';
import { CommonActions, CommonFormLabels } from '../../../localization/helpers.js';
let NetworkCreatePage = class NetworkCreatePage extends LitElement {
    constructor() {
        super(...arguments);
        this.currentStep = 1;
        this.steps = [];
        this.errorMessage = null;
        this.dataFrame = null;
        this.importFile = null;
        this.newtorkCreate = {
            name: null,
            description: null,
            network_type_id: null,
            logo: null,
        };
    }
    connectedCallback() {
        super.connectedCallback();
        this.steps = [
            msg('Network Information', { id: 'network.create.step1', desc: 'Network creation step 1' }),
            msg('Import File', { id: 'network.create.step2', desc: 'Network creation step 2' }),
            msg('Configuration', { id: 'network.create.step3', desc: 'Network creation step 3' }),
        ];
    }
    renderStep1() {
        if (!this.newtorkCreate.network_type_id) {
            this.newtorkCreate.network_type_id =
                this.networkTypes.length > 0
                    ? this.networkTypes[0].network_type_id
                    : null;
        }
        return html `
      <p>
        ${msg('Enter a name, description, and network type for the network', { id: 'network.create.instruction', desc: 'Network creation instruction' })}
      </p>
      <label for="network-name">${msg('Network Name', { id: 'network.label.name', desc: 'Network name label' })}</label>
      <input-text
        id="network-name"
        label=${msg('Network Name', { id: 'network.label.name', desc: 'Network name label' })}
        size="large"
        placeholder=${msg('Enter network name', { id: 'network.placeholder.name', desc: 'Network name placeholder' })}
        .value=${this.newtorkCreate.name || ''}
        @input-text=${(e) => (this.newtorkCreate.name = e.target.value)}
      ></input-text>
      <label for="network-description">${CommonFormLabels.description()}</label>
      <input-text-area
        id="network-description"
        label=${CommonFormLabels.description()}
        .value=${this.newtorkCreate.description || ''}
        placeholder=${msg('Enter a description for the network', { id: 'network.placeholder.description', desc: 'Network description placeholder' })}
        @input-text-area=${(e) => (this.newtorkCreate.description = e.target.value)}
      ></input-text-area>
      <label for="network-type">${msg('Network Type', { id: 'network.label.type', desc: 'Network type label' })}</label>
      <select-input
        id="network-type"
        label=${msg('Network Type', { id: 'network.label.type', desc: 'Network type label' })}
        .value=${this.newtorkCreate.network_type_id || ''}
        .options=${this.networkTypes.map((type) => ({
            value: type.network_type_id,
            label: type.name,
        }))}
        placeholder=${msg('Select network type', { id: 'network.placeholder.selectType', desc: 'Network type placeholder' })}
        @select-change=${(e) => (this.newtorkCreate.network_type_id = e.detail.value)}
      ></select-input>
    `;
    }
    renderStep2() {
        return html `
      <p>
        ${msg('Import a file for the network', { id: 'network.import.instruction', desc: 'Import file instruction' })}
        <strong>${this.newtorkCreate.name}</strong>. ${msg('Supported formats: CSV and Excel.', { id: 'network.import.supportedFormats', desc: 'Supported file formats' })}
      </p>
      <label for="network-file">${msg('Network File', { id: 'network.label.file', desc: 'Network file label' })}</label>
      <input-file
        id="network-file"
        label=${msg('Network File', { id: 'network.label.file', desc: 'Network file label' })}
        .fileTypes=${['.csv', '.xlsx']}
        @file-selected=${(e) => {
            this.importFile = e.detail[0];
        }}
      ></input-file>
    `;
    }
    renderStep3() {
        return html `
      <p>
        ${msg('Configure the network settings for', { id: 'network.config.instruction', desc: 'Configuration instruction' })}
        <strong>${this.newtorkCreate.name}</strong>.
      </p>
      <div class="col-container">
        <file-table
          class="col"
          .file=${this.importFile}
          .rowsPerPage=${20}
          @dataframe-loaded=${(e) => {
            this.dataFrame = e.detail;
        }}
        ></file-table>
        <network-field-manager
          class="col"
          .dataFrame=${this.renderRoot.querySelector('file-table')?.getDataFrame()}
          .networkFields=${basicPoiFields}
        ></network-field-manager>
      </div>
    `;
    }
    handleSubmit() {
        // Get the network fields from the NetworkFieldManager
        const networkFieldManager = this.renderRoot.querySelector('network-field-manager');
        const { renameDict, networkFields } = networkFieldManager.getNetworkFields();
        this.dataFrame.renameColumns(renameDict);
        this.dataFrame.changeType('id', 'string');
        const payload = poisCreateFromDataFrame(this.dataFrame, networkFields);
        this.dispatchEvent(new CustomEvent('network-created', {
            detail: {
                networkCreate: this.newtorkCreate,
                networkFields: networkFields,
                poisCreate: payload,
            },
        }));
    }
    handleNext() {
        if (this.currentStep === 1 &&
            (this.newtorkCreate.name === null ||
                this.newtorkCreate.name.trim() === '' ||
                this.newtorkCreate.network_type_id === null)) {
            this.errorMessage = msg('Network name and type are required', { id: 'error.network.nameAndTypeRequired', desc: 'Network name and type required error' });
            return;
        }
        if (this.currentStep === 2 && this.importFile === null) {
            this.errorMessage = msg('An import file is required', { id: 'error.network.importFileRequired', desc: 'Import file required error' });
            return;
        }
        if (this.currentStep === 3) {
            // const { renameDict, networkFields } = (
            const { networkFields } = this.renderRoot.querySelector('network-field-manager').getNetworkFields();
            if (!networkFields.find((nf) => nf.name === 'id' && nf.native)) {
                this.errorMessage = msg('A unique identifier is required for the network', { id: 'error.network.idRequired', desc: 'Unique ID required error' });
                return;
            }
            this.handleSubmit();
            return;
        }
        if (this.currentStep < this.steps.length) {
            this.errorMessage = null;
            this.currentStep++;
        }
    }
    handlePrevious() {
        if (this.currentStep > 1) {
            this.currentStep--;
        }
    }
    render() {
        return html `
      <full-screen-modal>
        <div id="background-gradient"></div>
        <div id="inside-modal">
          <basic-card align="center" class="card">
            <h2>${msg('Create a new network', { id: 'network.create.title', desc: 'Create network title' })}</h2>
            <form-stepper
              .currentStep=${this.currentStep}
              .steps=${this.steps}
            ></form-stepper>
            <div class="step">
              ${this.currentStep === 1 ? this.renderStep1() : ''}
              ${this.currentStep === 2 ? this.renderStep2() : ''}
              ${this.currentStep === 3 ? this.renderStep3() : ''}
            </div>
            ${this.errorMessage
            ? html `<p style="color: red;">${this.errorMessage}</p>`
            : ''}
            <div class="buttons">
              ${this.currentStep > 1
            ? html `<simple-button
                    label=${CommonActions.previous()}
                    variant="secondary"
                    @button-click=${this.handlePrevious}
                  ></simple-button>`
            : ''}
              <simple-button
                special
                variant="primary"
                label=${CommonActions.next()}
                @button-click=${this.handleNext}
              ></simple-button>
            </div>
          </basic-card>
        </div>
      </full-screen-modal>
    `;
    }
};
NetworkCreatePage.styles = css `
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }

    #background-gradient {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      background: linear-gradient(135deg, #46c56cff, #4b7afcff);
      z-index: 1; /* Place the background below the content */
    }

    #inside-modal {
      position: absolute;
      z-index: 2; /* Place the content above the background */
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
    }

    .card {
      min-width: 700px;
      max-width: 80%;
      max-height: 90%;
    }

    label {
      display: block;
      text-align: left; /* Align the label to the left */
      margin-top: 15px;
      margin-bottom: 8px; /* Add some spacing below the label */
    }

    .buttons {
      margin-top: 20px;
      display: flex;
      justify-content: flex-end; /* Align buttons to the right */
      gap: 10px; /* Add spacing between buttons */
    }

    .col-container {
      display: flex;
      flex-wrap: wrap; /* Allow columns to wrap if needed */
      gap: 20px;
      max-width: 100%; /* Prevent the container from exceeding the parent width */
      box-sizing: border-box;
      align-items: stretch; /* Ensure columns stretch to the same height */
      height: calc(100vh - 375px);
    }

    .col {
      flex: 1; /* Ensure columns take equal space */
      min-width: 300px; /* Set a minimum width to prevent columns from becoming too small */
      max-width: 100%; /* Prevent columns from exceeding the container width */
      box-sizing: border-box; /* Include padding and border in width calculation */
      max-height: 100%;
      overflow: auto;
      width: 1500px;
    }

    file-table {
      width: 100%; /* Adjust to fit the container */
    }
  `;
__decorate([
    property({ type: Number })
], NetworkCreatePage.prototype, "currentStep", void 0);
__decorate([
    property({ type: Array })
], NetworkCreatePage.prototype, "steps", void 0);
__decorate([
    property({ type: Object })
], NetworkCreatePage.prototype, "networkTypes", void 0);
__decorate([
    state()
], NetworkCreatePage.prototype, "errorMessage", void 0);
__decorate([
    state()
], NetworkCreatePage.prototype, "dataFrame", void 0);
NetworkCreatePage = __decorate([
    localized(),
    customElement('network-create-page')
], NetworkCreatePage);
export { NetworkCreatePage };
//# sourceMappingURL=NetworkCreatePage.js.map